import { Box, Card, CardContent, Skeleton, Stack, Typography } from "@mui/material";
import {
    Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer,
    Tooltip, XAxis, YAxis,
} from "recharts";
import dayjs from "dayjs";
import { CreditoContadoDia } from "@/features/dashboard/api/get-dashboard-data";

interface Props {
    title: string;
    data?: CreditoContadoDia[];
    loading: boolean;
    field1: string;
    field2: string;
    label1: string;
    label2: string;
    color1?: string;
    color2?: string;
    currencySymbol?: string;
    dateFormat?: string;
}

interface TooltipData {
    active?: boolean;
    payload?: { name?: string; value?: number; color?: string }[];
    label?: string;
    currencySymbol?: string;
}

function CustomTooltip({ active, payload, label, currencySymbol }: TooltipData) {
    if (!active || !payload?.length) return null;
    const fmt = (v?: number) =>
        currencySymbol ? `${currencySymbol} ${v?.toLocaleString("es-PY")}` : v?.toLocaleString("es-PY");
    return (
        <Box sx={{
            bgcolor: "grey.900", color: "common.white",
            px: 1.5, py: 1, borderRadius: "8px",
            fontSize: "0.8125rem", fontWeight: 500,
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        }}>
            <Box sx={{ color: "grey.400", fontSize: "0.75rem", mb: 0.5 }}>
                {dayjs(label).format("DD MMM YYYY")}
            </Box>
            {payload.map((p, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color }} />
                    <Box sx={{ color: "grey.300", mr: 0.5 }}>{p.name}:</Box>
                    <Box>{fmt(p.value)}</Box>
                </Stack>
            ))}
        </Box>
    );
}

function EmptyState() {
    return (
        <Box sx={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="body2" color="text.disabled">Sin datos para el período</Typography>
        </Box>
    );
}

export default function DualLineChartCard({
    title, data, loading,
    field1, field2, label1, label2,
    color1 = "#635bff", color2 = "#15b79f",
    currencySymbol, dateFormat = "DD/MM",
}: Props) {
    const isEmpty = !loading && (!data || data.length === 0);

    const formatted = data?.map((d) => ({
        credito: d.credito,
        contado: d.contado,
        date: d.date,
        dateLabel: dayjs(d.date).format(dateFormat),
    }));

    const gradId1 = `grad-${color1.replace("#", "")}`;
    const gradId2 = `grad-${color2.replace("#", "")}`;

    return (
        <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CardContent sx={{ pb: "16px !important", flex: 1, display: "flex", flexDirection: "column" }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 1.5 }}>
                    {title}
                </Typography>

                {loading ? (
                    <Skeleton variant="rectangular" sx={{ borderRadius: "8px", flex: 1, minHeight: 220 }} />
                ) : isEmpty ? (
                    <EmptyState />
                ) : (
                    <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={formatted} margin={{ left: 8, right: 8, top: currencySymbol ? 24 : 4, bottom: 4 }}>
                            <defs>
                                <linearGradient id={gradId1} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={color1} stopOpacity={0.18} />
                                    <stop offset="100%" stopColor={color1} stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id={gradId2} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={color2} stopOpacity={0.18} />
                                    <stop offset="100%" stopColor={color2} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid horizontal vertical={false} stroke="var(--mui-palette-divider)" strokeDasharray="0" />
                            <XAxis
                                dataKey="dateLabel"
                                tick={{ fontSize: 11, fill: "var(--mui-palette-text-secondary)" }}
                                axisLine={false} tickLine={false}
                                interval="preserveStartEnd"
                            />
                            <YAxis
                                tick={{ fontSize: 11, fill: "var(--mui-palette-text-secondary)" }}
                                axisLine={false} tickLine={false}
                                tickFormatter={(v: number) => currencySymbol ? `${currencySymbol} ${v.toLocaleString("es-PY")}` : v.toLocaleString("es-PY")}
                            />
                            <Tooltip content={<CustomTooltip currencySymbol={currencySymbol} />} />
                            <Legend
                                iconType="circle" iconSize={8}
                                wrapperStyle={{ fontSize: "0.75rem", paddingTop: 8 }}
                            />
                            <Area
                                type="monotone" dataKey={field1} name={label1}
                                stroke={color1} strokeWidth={2}
                                fill={`url(#${gradId1})`}
                                dot={false} activeDot={{ r: 4, fill: color1, strokeWidth: 0 }}
                            />
                            <Area
                                type="monotone" dataKey={field2} name={label2}
                                stroke={color2} strokeWidth={2}
                                fill={`url(#${gradId2})`}
                                dot={false} activeDot={{ r: 4, fill: color2, strokeWidth: 0 }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
}
